import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { type CSSProperties, type Ref, useState } from 'react';
import { useHover, offset, useFloating, useFocus, useInteractions, useMergeRefs } from '@floating-ui/react';

import { Icon } from '@flintmint/web-shared/components/icon';

import { hexToRGB } from '@/utils/helper-utils';

import type { Tag } from '@flintmint/core-shared/rxdb';

import styles from './tag-chip.module.scss';

export const TagChip = (props: TagChipProps) => {
  const { tag, ref, draggable, hidden, onDelete, dragging, dragOverlay, style, ...rest } = props;

  const [showDelete, setShowDelete] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: showDelete,
    onOpenChange: setShowDelete,
    middleware: [
      offset({
        mainAxis: -10,
        crossAxis: 10,
      }),
    ],
    placement: 'top-end',
  });

  const hover = useHover(context, {
    move: false,
    delay: {
      open: 200,
      close: 0,
    },
  });
  const focus = useFocus(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus]);

  return (
    <div
      className={clsx(styles.tagChipHolder, {
        [styles.dragOverlay]: dragOverlay,
        [styles.draggable]: draggable,
      })}
      data-testid={`${tag.name}-tag-${hidden ? 'hidden' : 'visible'}`}
    >
      <div
        className={clsx(styles.tagChip, {
          [styles.hidden]: hidden,
          [styles.dragging]: dragging,
        })}
        aria-label={tag.name}
        tabIndex={hidden ? -1 : 0}
        style={
          {
            ['--color']: tag.color,
            ['--rgb']: hexToRGB(tag.color),
            ...style,
          } as CSSProperties
        }
        ref={useMergeRefs([refs.setReference, ref])}
        {...getReferenceProps()}
        {...rest}
      >
        <span className="ellipsis-text">{tag.name}</span>
        <AnimatePresence>
          {!!onDelete && showDelete && !dragging && !dragOverlay && (
            <motion.span
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                transition: {
                  delay: 0.3,
                },
              }}
              exit={{
                opacity: 0,
                transition: {
                  delay: 0,
                },
              }}
              // cancels drag on the delete icon
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                setShowDelete(false);
                onDelete?.(tag);
              }}
              className={styles.deleteBtn}
              data-testid="tag-remove-icon"
            >
              <Icon size={12} type="multiplication-sign" />
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export interface TagChipProps {
  tag: Tag;
  ref?: Ref<HTMLElement>;
  dragOverlay?: boolean;
  dragging?: boolean;
  draggable?: boolean;
  style?: CSSProperties;
  hidden?: boolean;
  deletable?: boolean;
  onDelete?: (tag: Tag) => void;
}
